import type { ApiErrorBody } from '@albuelis-accounting/shared';

/**
 * Minimal typed fetch wrapper for the backend API. Pages call these helpers (or the
 * typed functions built on them in `api/*.ts`), never `fetch` directly.
 *
 * Wiring (done once by the app shell): `configureApi({ baseUrl, getSessionValue })`.
 */
let baseUrl = '';
let getSessionValue: () => Promise<string | undefined> = async () => undefined;

export function configureApi(options: {
  baseUrl: string;
  /** Returns the bearer value for the next request (ID token, or the local dev value). */
  getSessionValue: () => Promise<string | undefined>;
}): void {
  baseUrl = options.baseUrl.replace(/\/$/, '');
  getSessionValue = options.getSessionValue;
}

export class ApiClientError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly fields?: Record<string, string>,
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

export type Query = Record<string, string | number | boolean | undefined>;

export interface RequestOptions {
  query?: Query;
  body?: unknown;
  /** Public endpoints (`/public/*`) send no Authorization header. */
  anonymous?: boolean;
  signal?: AbortSignal;
}

export async function apiRequest<T>(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const url = new URL(`${baseUrl}${path}`);
  for (const [key, value] of Object.entries(options.query ?? {})) {
    if (value !== undefined && value !== '') url.searchParams.set(key, String(value));
  }

  const headers: Record<string, string> = {};
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  if (!options.anonymous) {
    const session = await getSessionValue();
    if (session) headers['Authorization'] = `Bearer ${session}`;
  }

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      headers,
      ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new ApiClientError(
      0,
      'network_error',
      'Could not reach the server. Check your connection and try again.',
    );
  }

  if (response.status === 204) return undefined as T;
  const text = await response.text();
  const parsed = text ? (JSON.parse(text) as unknown) : undefined;

  if (!response.ok) {
    const error = (parsed as ApiErrorBody | undefined)?.error;
    throw new ApiClientError(
      response.status,
      error?.code ?? 'error',
      error?.message ?? `The request failed (${response.status}).`,
      error?.fields,
      error?.requestId,
    );
  }
  return parsed as T;
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => apiRequest<T>('GET', path, options),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiRequest<T>('POST', path, { ...options, ...(body !== undefined ? { body } : {}) }),
  put: <T>(path: string, body: unknown, options?: RequestOptions) =>
    apiRequest<T>('PUT', path, { ...options, body }),
  patch: <T>(path: string, body: unknown, options?: RequestOptions) =>
    apiRequest<T>('PATCH', path, { ...options, body }),
  delete: <T = void>(path: string, options?: RequestOptions) =>
    apiRequest<T>('DELETE', path, options),
};
